/**
 * The homepage intro sequence.
 *
 * Runs once per visit, on a load of the homepage that starts at the top of the
 * page. A refresh from part-way down is left alone, and so is every homepage
 * load after the first until the visitor leaves the site and comes back.
 *
 * The hero's background images are already there; the wordmark fades up over
 * them, its letters leave one at a time, the heading's first word arrives
 * centred and the line slides left as the rest of it follows, and the header
 * comes in last. Scroll is held throughout. About 4.8 seconds end to end.
 *
 * WHAT THIS FILE DOES NOT DO IS THE IMPORTANT PART, the same as assets/reveal.js.
 * Every rule that HIDES anything lives in assets/critical.css behind
 * `html[data-intro]`, and that attribute is set by exactly one thing — the inline
 * boot guard in layout/theme.liquid, which also flips it to "done" after four
 * seconds if this file never registered `window.themeIntro`. This file only ever
 * moves the sequence FORWARD. A 404, a parse error or a throw all fail toward a
 * fully visible page.
 *
 * `window.themeIntro` is claimed as the first statement below, before anything
 * that could throw, because the deadman checks for it by name.
 *
 * THE CHOREOGRAPHY IS NOT HERE. It is transition-delays in critical.css; this
 * file flips one attribute and waits. So retiming the sequence means editing the
 * custom properties there, and the only number in this file that has to agree
 * with them is TOTAL_MS.
 *
 * WHY THIS DOES consume window.themeScroll, where assets/reveal.js deliberately
 * does not: the sequence holds the page still while it plays, and Lenis is the
 * only thing that can be asked to stop. Locking scroll any other way would fight
 * it. That means the documented `themescroll:ready` handshake applies here.
 *
 * ON THE RELATIONSHIP WITH THE SITE-WIDE REVEAL. Both are running on this page.
 * The reveal still owns every section below the hero; the hero itself is exempted
 * from it by a specificity win in critical.css, so its photographs are up at
 * first paint and the sequence opens on them rather than fading them in. That
 * exemption is also what keeps the reveal's fade off this page's LCP.
 * Nothing here touches reveal.js or its state.
 */
(function () {
  'use strict';

  /**
   * Claim the flag first. The boot guard removes the hidden state after four
   * seconds if this is still undefined, so anything above this line that threw
   * would blank the header and hero text for four seconds.
   */
  var themeIntro = {};
  window.themeIntro = themeIntro;

  var LOCK_KEY = 'intro';
  var SEEN_KEY = 'aw:intro';

  /**
   * Must outlast the last transition declared in critical.css:
   * --intro-chrome-start (4250ms) + --intro-chrome-duration (450ms). The 100ms
   * on top is slack for the frame the state flip lands on, not a magic number.
   *
   * THIS IS ALSO WHEN SCROLL COMES BACK, which is the reason it must not be
   * trimmed to "about right": the brief is that nothing scrolls until the
   * header is FULLY in, so this has to sit past the end of that fade, not on
   * top of it.
   *
   * It is unrelated to the boot guard's 4000ms deadman in layout/theme.liquid,
   * despite now being longer than it. That timer only asks whether this file
   * registered at all, which happens on line one — a running sequence has
   * already satisfied it.
   */
  var TOTAL_MS = 4800;

  /** Matches --intro-skip in critical.css. */
  var SKIP_MS = 200;

  /**
   * Beyond this, stop waiting for webfonts and start anyway. Gating the sequence
   * on document.fonts.ready is what stops the wordmark fading up in Georgia and
   * then reflowing into Louize Display a moment later — but a font that never
   * arrives must not hold the page hostage.
   */
  var FONT_TIMEOUT_MS = 1000;

  /**
   * WHEEL AND TOUCHSTART ARE DELIBERATELY NOT IN THIS LIST ANY MORE.
   *
   * The brief is that nothing scrolls until the header is fully in, so a scroll
   * or a swipe has to do NOTHING — while they skipped the sequence, scrolling
   * was in practice a way to get past it, which is the opposite of a lock.
   * `themeScroll.lock()` already stops the page moving; this stops the attempt
   * from ending the intro early as well.
   *
   * A key and a pointer press stay, as the escape hatch. Keyboard especially:
   * a keyboard visitor must never be held in something they cannot leave.
   *
   * ON WCAG 2.2.2 (Pause, Stop, Hide): it applies to auto-playing motion
   * lasting more than five seconds. The sequence ends at ~4.8s including the
   * unlock, so it sits under that line by about 200ms. Anything that pushes
   * the total past 5s makes a visible pause control a requirement rather than a
   * choice, so treat 5s as a hard ceiling on the timings in critical.css.
   */
  var SKIP_EVENTS = ['keydown', 'pointerdown'];

  /**
   * `?intro=1` turns on both the replay and a running commentary.
   *
   * A sequence that plays once per session and then silently does nothing is
   * genuinely hard to debug from the outside — "no animation" has half a dozen
   * causes here that look identical on screen, and the difference between them
   * is which branch was taken. So the branches say so, behind a query parameter
   * a visitor will never have. Nothing logs on a normal load.
   */
  var DEBUG = window.location.search.indexOf('intro=1') !== -1;

  function log(message) {
    if (DEBUG) console.log('[intro] ' + message);
  }

  if (!customElements.get('home-intro')) {
    customElements.define(
      'home-intro',
      class HomeIntro extends HTMLElement {
        connectedCallback() {
          this.root = document.documentElement;
          this.locked = false;
          this.finished = false;
          this.timers = [];

          this.handleSkip = this.handleSkip.bind(this);
          this.start = this.start.bind(this);

          /*
           * THE LAST HOLE IN THE SAFETY STORY, closed here.
           *
           * The boot guard's deadman only asks whether `window.themeIntro`
           * exists. That is claimed at the top of this file, before any work —
           * so it catches the script never arriving, but NOT the script
           * arriving and then throwing, which would leave the header and the
           * hero's text hidden with the deadman satisfied and nothing else
           * coming. Setup runs inside this try; anything unexpected stands the
           * feature down and gives the page back.
           */
          try {
            this.setUp();
          } catch (error) {
            console.warn('[intro] setup failed, recovering the page', error);
            this.abort('threw during setup');
          }
        }

        setUp() {
          /*
           * Nothing to do unless the boot guard decided this load qualifies.
           * It owns the homepage check, the setting and reduced motion;
           * re-testing them here would be a second opinion that could disagree
           * with the one the CSS is already acting on.
           *
           * REMOVE THE OVERLAY ON THE WAY OUT rather than just returning. The
           * Liquid renders it on every homepage load, but whether an intro
           * actually happens is decided here, in the browser — so on any path
           * where it does not, something has to take the wordmark away.
           * Returning quietly left it sitting over the page permanently.
           * critical.css also hides it for this case, which is what covers the
           * version of this where the script never runs at all; this is the
           * tidy-up, not the guarantee.
           */
          if (this.root.getAttribute('data-intro') !== 'pending') {
            this.abort(
              'boot guard did not arm this load — already seen this visit, reduced motion, setting off, or the deadman already fired'
            );
            return;
          }

          log('upgraded, sequence starting');

          /*
           * A reload restores the previous scroll position. Playing an opening
           * sequence over a view halfway down the page is worse than not
           * playing it, and the scroll lock would strand the visitor where
           * they were. Bail before locking anything.
           */
          if (window.scrollY !== 0) {
            this.abort('page loaded already scrolled — an opening sequence over a mid-page view is worse than none');
            return;
          }

          this.hero = document.querySelector('[data-intro-hero]');
          this.content = this.hero && this.hero.querySelector('[data-intro-content]');

          if (!this.content) {
            this.abort('no [data-intro-hero] with a [data-intro-content] on this page — is the hero still the first section?');
            return;
          }

          this.splitHeading();

          for (var i = 0; i < SKIP_EVENTS.length; i++) {
            window.addEventListener(SKIP_EVENTS[i], this.handleSkip, { passive: true });
          }

          /*
           * The catch is the async half of the try in connectedCallback —
           * a throw past this point is a rejected promise, not an exception,
           * so it would otherwise escape as an unhandled rejection and leave
           * the page hidden with the deadman already satisfied.
           */
          this.waitForFonts()
            .then(this.start)
            .catch(
              function (error) {
                console.warn('[intro] sequence failed, recovering the page', error);
                this.abort('threw while starting');
              }.bind(this)
            );
        }

        disconnectedCallback() {
          this.teardown();
          this.unlockScroll();
        }

        /**
         * Wrap each word of the hero heading so they can arrive separately.
         *
         * The words are rejoined with real spaces, so the line still wraps
         * normally and the accessible name is unchanged — inline spans do not
         * break up a text node for a screen reader, and the original sentence
         * was in the HTML for crawlers before this ran regardless.
         *
         * The first word is marked so critical.css can give it an earlier
         * delay than the rest.
         */
        splitHeading() {
          var heading = this.content.querySelector('[data-block-kind="text"]');
          if (!heading) return;

          var words = heading.textContent.trim().split(/\s+/);
          if (!words.length || !words[0]) return;

          heading.textContent = '';

          for (var i = 0; i < words.length; i++) {
            if (i > 0) heading.appendChild(document.createTextNode(' '));

            var span = document.createElement('span');
            span.setAttribute('data-intro-word', i === 0 ? 'first' : '');
            span.textContent = words[i];
            heading.appendChild(span);
          }

          /*
           * Marking it LAST is deliberate. critical.css hides
           * `[data-intro-content] > *:not([data-intro-heading])`, so until this
           * attribute exists the heading is hidden along with everything else —
           * which is exactly what should be true while it holds unsplit text.
           * Setting it before the split would expose the whole sentence for a
           * frame.
           */
          heading.setAttribute('data-intro-heading', '');

          this.heading = heading;
          this.measureShift(heading);
        }

        /**
         * Work out how far the line has to travel.
         *
         * The first word is not centred on its own — the words after it are
         * invisible but still occupy width, so it is the LINE that is centred.
         * The whole heading therefore starts pushed right by however far it
         * takes to put the FIRST WORD'S centre on the heading's centre, and
         * animates back to zero. One composited property, no layout per frame.
         *
         * MEASURE FROM THE TWO CENTRES, NOT FROM THE ELEMENT'S WIDTH. The hero
         * forces `w-full` onto text blocks
         * (`[&>[data-block-kind=text]]:w-full` on the content column, which is
         * what makes text-align mean anything there) — so the heading box is
         * the full column width and has nothing to do with how wide the text
         * inside it is. Half the difference between THAT and the first word
         * throws the word far right of centre instead of onto it. Comparing
         * rendered centres is independent of how the box is sized.
         *
         * The result is positive: the first word sits left of centre while the
         * rest of the line occupies the space to its right, so it travels right
         * to get centred and then back left as the others arrive.
         *
         * Two cases where the premise does not hold and the shift is simply
         * dropped, leaving the words to fade in where they are:
         *
         *   - The heading wrapped, so "the line" is not one line.
         *   - The heading is not centred. `content_position` is a merchant
         *     setting with nine values, and "starts centred, moves left" only
         *     describes the three centred ones.
         */
        measureShift(heading) {
          var words = heading.querySelectorAll('[data-intro-word]');
          if (words.length < 2) return;

          if (window.getComputedStyle(heading).textAlign !== 'center') return;
          if (words[1].offsetTop !== words[0].offsetTop) return;

          var headingRect = heading.getBoundingClientRect();
          var wordRect = words[0].getBoundingClientRect();

          var shift =
            headingRect.left + headingRect.width / 2 - (wordRect.left + wordRect.width / 2);

          /* Sub-pixel means it is already centred — a one-word heading, say. */
          if (Math.abs(shift) < 1) return;

          /*
           * The one inline style JavaScript writes in this feature. It is a
           * measurement, so unlike every other custom-property bridge in the
           * theme it cannot come from Liquid.
           */
          heading.style.setProperty('--intro-shift', shift + 'px');
          log('split ' + words.length + ' words, shift ' + Math.round(shift) + 'px');
        }

        /**
         * The documented themeScroll handshake — the controller may not have
         * initialised yet, in which case it announces itself once.
         */
        lockScroll() {
          if (this.locked) return;
          this.locked = true;

          if (window.themeScroll) {
            window.themeScroll.lock(LOCK_KEY);
            return;
          }

          this.pendingLock = function () {
            if (window.themeScroll) window.themeScroll.lock(LOCK_KEY);
          };
          document.addEventListener('themescroll:ready', this.pendingLock, { once: true });
        }

        unlockScroll() {
          if (!this.locked) return;
          this.locked = false;

          if (this.pendingLock) {
            document.removeEventListener('themescroll:ready', this.pendingLock);
            this.pendingLock = null;
          }

          if (window.themeScroll) window.themeScroll.unlock(LOCK_KEY);
        }

        /**
         * Resolve once the webfonts are in, or once the wait has gone on long
         * enough to be a problem in its own right.
         */
        waitForFonts() {
          var wait = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

          return Promise.race([
            wait,
            new Promise(
              function (resolve) {
                this.timers.push(setTimeout(resolve, FONT_TIMEOUT_MS));
              }.bind(this)
            ),
          ]);
        }

        /**
         * Flip the state that starts every transition at once.
         *
         * The extra frame matters: the elements have to be painted in their
         * initial state at least once for the transitions to have somewhere to
         * run from. Set it in the same frame the split happened and the browser
         * coalesces both into one style recalculation, so the whole sequence
         * snaps to its end state with no motion at all — the same trap
         * assets/reveal.js documents at scan().
         */
        start() {
          if (this.finished || this.root.getAttribute('data-intro') !== 'pending') return;

          requestAnimationFrame(
            function () {
              if (this.finished) return;

              /*
               * SCROLL POSITION IS CHECKED TWICE, AND THE LOCK IS TAKEN HERE
               * RATHER THAN DURING SETUP, both for the same reason: the browser
               * restores a reload's scroll position around the time deferred
               * scripts run, so the check in setUp() can pass and the page jump
               * a moment later. Locking early would then have thrown away the
               * position the visitor was actually at — `themeScroll.lock()`
               * puts `overflow-hidden` on <html>, which resets it.
               *
               * Deciding at the last possible moment gives restoration the most
               * time to land, and means nothing is locked until an intro is
               * genuinely about to play.
               */
              if (window.scrollY !== 0) {
                this.abort('scroll position restored after setup — not playing over a mid-page view');
                return;
              }

              this.lockScroll();
              this.root.setAttribute('data-intro', 'running');
              this.markSeen();
              log('running');
              this.timers.push(setTimeout(this.finish.bind(this), TOTAL_MS));
            }.bind(this)
          );
        }

        /**
         * Remember this visit has had its showing.
         *
         * CALLED WHEN THE SEQUENCE STARTS, NOT WHEN IT ENDS. Writing it in
         * finish() meant a visitor who navigated away part-way through had
         * never "seen" it by this flag's reckoning, and got the whole thing
         * again on their next homepage load. Starting is what counts.
         *
         * Deliberately NOT called from abort(): a load that declined to play
         * must not burn the visit's one showing on nothing. That was a real bug
         * once — see the QA log.
         *
         * Skipped in the theme editor, so a merchant can preview the thing they
         * just switched on by reloading rather than by clearing storage.
         */
        markSeen() {
          if (window.Shopify && window.Shopify.designMode) return;

          try {
            sessionStorage.setItem(SEEN_KEY, '1');
          } catch (error) {
            /* Private browsing refuses to write. The intro simply replays. */
          }
        }

        /**
         * Any interaction ends the sequence early: everything runs to the state
         * it was heading for, quickly, and scroll comes back.
         */
        handleSkip() {
          if (this.finished || this.root.getAttribute('data-intro') === 'skipping') return;

          this.teardown();
          this.root.setAttribute('data-intro', 'skipping');
          this.timers.push(setTimeout(this.finish.bind(this), SKIP_MS));
        }

        /**
         * Stand down without having played anything.
         *
         * Does not touch `data-intro` when the attribute was never armed:
         * the CSS hides the overlay for `html:not([data-intro])` already, and
         * writing "done" onto a page that was never pending would be inventing
         * a state nothing asked for.
         */
        abort(reason) {
          log('standing down — ' + reason);

          this.finished = true;
          this.teardown();
          this.unlockScroll();

          if (this.root.hasAttribute('data-intro')) {
            this.root.setAttribute('data-intro', 'done');
          }

          if (this.parentNode) this.parentNode.removeChild(this);
        }

        /**
         * The only exit for a sequence that actually ran. Idempotent, because
         * the timer, the skip handler and disconnectedCallback can all reach it.
         *
         * "done" matches none of the rules in critical.css, so the page is back
         * to exactly what it rendered as before this feature existed. The
         * overlay goes with it — there is nothing left to look at, and leaving
         * a fixed full-viewport element in the DOM invites exactly the kind of
         * click-swallowing bug the grain layer's comment warns about.
         */
        finish() {
          if (this.finished) return;
          this.finished = true;

          this.teardown();
          this.root.setAttribute('data-intro', 'done');
          this.unlockScroll();

          if (this.parentNode) this.parentNode.removeChild(this);
        }

        teardown() {
          for (var i = 0; i < SKIP_EVENTS.length; i++) {
            window.removeEventListener(SKIP_EVENTS[i], this.handleSkip);
          }

          for (var t = 0; t < this.timers.length; t++) {
            clearTimeout(this.timers[t]);
          }
          this.timers = [];
        }
      }
    );
  }
})();
